import React, { useRef, useState } from 'react';

import classes from './styles.module.scss';
import AdminService from '../../../../../../../services/AdminService';

const ACCEPT = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
].join(',');

export default function MultiFileUploader({
  name,
  label,
  placeholderTitle,
  placeholderHint,
  values = [],
  setFieldValue,
  setFieldTouched,
  readonly,
  height = 110,
  uploadImmediately = false,
  uploadingText,
  uploadFailedText,
}) {
  const inputRef = useRef();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const toMeta = (uploaded, fallback) => ({
    fileName: uploaded?.fileName || fallback?.name,
    url: uploaded?.uri || uploaded?.url,
    mimeType: uploaded?.mimeType || fallback?.type,
    displayName: uploaded?.displayName || fallback?.name,
  });

  const handleAddFiles = async (files) => {
    if (!files?.length) return;
    if (!uploadImmediately) {
      const next = [...(values || []), ...files];
      setFieldValue?.(name, next);
      setFieldTouched?.(name, true, true);
      return;
    }
    try {
      setIsUploading(true);
      setUploadError('');
      const uploadedMetas = [];
      // Upload sequentially to keep simple progress; could be parallel if API allows
      // eslint-disable-next-line no-restricted-syntax
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const uploaded = await AdminService.uploadFile(file);
        uploadedMetas.push(toMeta(uploaded, file));
      }
      const next = [...(values || []), ...uploadedMetas];
      setFieldValue?.(name, next);
      setFieldTouched?.(name, true, true);
    } catch (e) {
      setUploadError(uploadFailedText || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const onSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    handleAddFiles(files);
    e.target.value = '';
  };

  const removeAt = (idx) => {
    const next = (values || []).filter((_, i) => i !== idx);
    setFieldValue?.(name, next);
    setFieldTouched?.(name, true, true);
  };

  return (
    <div className={classes.Uploader}>
      {label && <span className={classes.label}>{label}</span>}
      <div
        className={classes.box}
        style={{ height }}
        onClick={() => {
          if (readonly) return;
          inputRef.current?.click();
        }}
      >
        {!readonly && (
          <button
            type="button"
            className={classes.addBtn}
            aria-label="Add"
            onClick={(e) => {
              e.stopPropagation();
              inputRef.current?.click();
            }}
          >
            +
          </button>
        )}
        {values?.length ? (
          <div className={classes.fileList}>
            {values.map((f, i) => (
              <div
                key={`${f?.name || f?.displayName || 'file'}-${
                  f?.lastModified || f?.size || 'na'
                }`}
                className={classes.fileRow}
              >
                <span className={classes.fileName}>
                  {f?.name || f?.displayName}
                </span>
                {!readonly && (
                  <button
                    type="button"
                    className={classes.deleteBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeAt(i);
                    }}
                    aria-label="Remove file"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className={classes.placeholder}>
            <div className={classes.title}>{placeholderTitle}</div>
            <div className={classes.hint}>
              {isUploading ? uploadingText || 'Uploading...' : placeholderHint}
            </div>
            {uploadError && (
              <div className={classes.hint} style={{ color: '#d33' }}>
                {uploadError}
              </div>
            )}
            {!readonly && (
              <button type="button" className={classes.addBtn} aria-label="Add">
                +
              </button>
            )}
          </div>
        )}
      </div>
      <input
        type="file"
        multiple
        ref={inputRef}
        accept={ACCEPT}
        onChange={onSelect}
        style={{ display: 'none' }}
      />
    </div>
  );
}
